<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

    <!--Filter Data Modal (shared: FilterModal.vue)-->
    <FilterModal
        title="Filter tags"
        :draft="filterDraft"
        v-model:search-by="tableData.searchBy"
        :view-options="viewOptions"
        :search-by-options="searchByOptions"
        :alert="defaultAlert"
        @search-by-change="changePlaceholder()"
        @apply="applyFilters"
        @clear="clearFilters"
    >
        <!-- Module: one or more (the list comes from tagModules() in global_functions.php) -->
        <SelectField
            v-model="filterDraft.modules"
            col="mb-3"
            label="Module"
            icon="fa fa-th-large"
            class="multiSelect-validation-field"
            :options="moduleOptions"
            :reduce="option => option.value"
            :close-on-select="false"
            placeholder="All modules"
            multiple
        />
    </FilterModal>
    <!--End Filter Data Modal-->

    <!--Begin form-->
    <RecordFormModal
        :title="isEditing ? 'Update Tag' : 'Add Tag'"
        title-icon="fa fa-tag"
        :is-editing="isEditing"
        :is-saving="isSaving"
        :alert="defaultAlert"
        @save="saveOrEditRecord"
        @sample="addSampleData"
        @reset="resetForm"
    >
        <HiddenField v-model="formInputs.id" />

        <div class="row g-3 mb-0">
            <FormField
                v-model="formInputs.name"
                col="col-md-8"
                label="Name"
                icon="fa fa-tag"
                placeholder="e.g. VIP"
                tooltip="Please enter the tag name"
                maxlength="100"
                required
                @input="handleInputChange"
                @change="handleInputChange"
            />
            <FormField
                v-model="formInputs.color"
                col="col-md-4"
                type="color"
                label="Colour"
                icon="fa fa-paint-brush"
                tooltip="Please choose a colour"
                required
                @input="handleInputChange"
                @change="handleInputChange"
            />
        </div>

        <div class="row g-3 mb-0">
            <SelectField
                v-model="formInputs.module"
                col="col-12"
                name="module"
                label="Module"
                icon="fa fa-th-large"
                :options="moduleOptions"
                :reduce="option => option.value"
                :disabled="isEditing"
                placeholder="Select module"
                tooltip="Please select the module"
                :hint="isEditing ? 'The module of an existing tag cannot be changed.' : 'The page whose records use this tag.'"
                :submitted="submitted"
                required
                @change="$clearValidationAlert('#recordForm')"
            />
        </div>

        <!-- Preview -->
        <div class="mt-3 mb-2">
            <span class="form-label sa-core-color me-2">Preview:</span>
            <span class="badge rounded-pill" :style="{ background: formInputs.color, color: tagTextColor(formInputs.color) }">{{ formInputs.name || "Tag name" }}</span>
        </div>
    </RecordFormModal>
    <!--End form-->

    <h4 class="sa-core-color fw-semibold mb-2"><i class="fa fa-tags me-2 opacity-75"></i>{{ pageTitle }}</h4>

    <!--Table-->
    <div class="card glass mt-0 bg-sa-card">
        <div class="card-body bg-sa-card-body p-0">
            <!-- Table action row (shared: TableToolbar.vue) -->
            <TableToolbar
                :can-add="$hasPermission('tags.add')"
                :filter-count="activeFilterCount"
                :columns="tableColumns"
                v-model:hidden-columns="hiddenColumns"
                :selected-count="tableData.selectedRecordIds.length"
                :deleted-view="tableData.recordFilter === 'deletedone'"
                :can-delete="$hasPermission('tags.delete')"
                @add="clearForm()"
                @filter="openFilters"
                @clear-selection="clearSelection"
                @bulk-action="runBulkAction"
            />

            <SearchBox
                v-model:search-query="tableData.searchQuery"
                v-model:records-per-page="tableData.recordsPerPage"
                :placeholder="tableData.searchPlaceHolderText"
                @search-input="onSearchInput"
                @search-click="onSearchButtonClick"
                @per-page-change="onRecordsPerPageChange"
            />

            <ActiveFilters
                :chips="activeFilterChips"
                @remove="removeFilterChip"
                @clear="clearAllFilterChips"
            />

            <div class="table-responsive">
                <table
                    class="table table-hover align-middle mb-0"
                    id="bootstrapTable"
                    tabindex="0"
                    @keydown.up="$moveRowSelection(-1, $event)"
                    @keydown.down="$moveRowSelection(1, $event)"
                    @keydown.enter="$onTableEnter($event)"
                >
                    <thead>
                        <tr>
                            <th>
                                <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
                            </th>
                            <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
                            <th class="button hide-sm-td">No</th>
                            <SortableTh v-if="isColumnVisible('name')" column="name" :table-data="tableData" @sort="orderBy">Tag</SortableTh>
                            <SortableTh v-if="isColumnVisible('module')" column="module" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Module</SortableTh>
                            <th v-if="isColumnVisible('used_count')" class="button hide-sm-td">Used on</th>
                            <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date</SortableTh>
                            <th class="button">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(record, index) in tableData.records" :key="record.id">
                            <tr
                                class="clickable-row"
                                :data-record-id="record.id"
                                :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                                @click="$onRowClick(record, $event)"
                                @dblclick="$onRowDblClick(record, $event)"
                            >
                                <td>
                                    <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
                                </td>
                                <td
                                    class="expand-cell hide-sm-td"
                                    :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                    @click="$toggleDetails(record.id)"
                                >
                                    <i
                                        class="fa fa-lg expand-icon ms-1"
                                        :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                                    ></i>
                                </td>
                                <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                                <td v-if="isColumnVisible('name')">
                                    <!-- Phones: + / − and the number go in front of the tag (their columns are hidden) -->
                                    <span
                                        class="show-sm-inline expand-cell me-2"
                                        :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                        @click="$toggleDetails(record.id)"
                                    >
                                        <i
                                            class="fa fa-lg expand-icon"
                                            :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                                        ></i>
                                    </span>
                                    <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
                                    <span class="badge rounded-pill" :style="{ background: record.color, color: tagTextColor(record.color) }">{{ record.name }}</span>
                                </td>
                                <td v-if="isColumnVisible('module')" class="hide-sm-td">{{ record.module_label }}</td>
                                <td v-if="isColumnVisible('used_count')" class="hide-sm-td">
                                    {{ record.used_count }} {{ record.used_count === 1 ? "record" : "records" }}
                                </td>
                                <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
                                <td>
                                    <RowActions
                                        :record="record"
                                        :modal-id="'deleteTagModal' + record.id"
                                        :can-edit="$hasPermission('tags.add')"
                                        :can-soft-delete="$hasPermission('tags.delete')"
                                        :can-restore="$hasPermission('tags.delete')"
                                        :can-delete-permanently="$hasPermission('tags.delete')"
                                        @edit="editRecord"
                                        @delete-type="setDeleteType"
                                    />
                                    <DeleteModal
                                        :modal-id="'deleteTagModal' + record.id"
                                        :title="deleteModalTitle"
                                        :message="deleteMessageFor(record)"
                                        @confirm="deleteRecord(record.id)"
                                    />
                                </td>
                            </tr>
                            <!-- Hidden expanded row -->
                            <tr class="collapse-row">
                                <td class="p-0"></td>
                                <td class="p-0 hide-sm-td"></td>
                                <td colspan="6" class="p-0">
                                    <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                                        <div class="p-3 details-list">
                                            <div><i class="fa fa-hashtag"></i><strong>Tag Number:</strong> {{ record.tag_number }}</div>
                                            <div :class="{ 'show-sm-td': isColumnVisible('module') }"><i class="fa fa-th-large"></i><strong>Module:</strong> {{ record.module_label }}</div>
                                            <div :class="{ 'show-sm-td': isColumnVisible('used_count') }"><i class="fa fa-tags"></i><strong>Used on:</strong> {{ record.used_count }}</div>
                                            <div><i class="fa fa-user"></i><strong>Created By:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                                            <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                                            <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                                            <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <!-- No rows: blank while loading, then the empty message in the middle -->
                        <tr v-if="tableData.records.length === 0" class="table-filler">
                            <td colspan="8" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                                <TableEmptyState
                                    v-if="!tableLoading"
                                    :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                                    :can-add="$hasPermission('tags.add')"
                                    :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No tags yet'"
                                    :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Tags help you group and find records, e.g. VIP or Urgent.'"
                                    icon="fa fa-tags"
                                    @add="clearForm()"
                                    @clear="clearAllFilterChips"
                                />
                            </td>
                        </tr>
                    </tbody>
                    <tfoot class="table-light">
                        <tr>
                            <td colspan="8">
                                <!-- Pagination (hidden when there are no rows; its space is kept) -->
                                <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                                <Pagination
                                    v-else
                                    :currentPage="tableData.currentPage"
                                    :totalPages="tableData.totalPages"
                                    :hasNextPage="tableData.hasNextPage"
                                    :totalRecords="tableData.totalRecords"
                                    :startRecord="tableData.startRecord"
                                    :endRecord="tableData.endRecord"
                                    :showFullPagination="tableData.totalRecords > 0"
                                    :lastKnownPage="lastKnownPage"
                                    @page-changed="fetchRecords"
                                    @loaderMessage="updateLoaderMessage"
                                    @last-page-request="goToLastPage"
                                />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
    <!--End Table-->
</template>

<script>
    import vSelect from "vue-select";
    import "vue-select/dist/vue-select.css";

    import PageStatus from "../../../components/admin/PageStatus.vue";
    import TableToolbar from "../../../components/admin/TableToolbar.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import SortableTh from "../../../components/admin/SortableTh.vue";
    import DeleteModal from "../../../components/admin/DeleteModal.vue";
    import RowActions from "../../../components/admin/RowActions.vue";
    import SearchBox from "../../../components/admin/SearchBox.vue";
    import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import HiddenField from "../../../components/admin/HiddenField.vue";
    import FilterModal from "../../../components/admin/FilterModal.vue";
    import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
    import tagsMixin from "./mixins/tagsMixin.js";
    import tableMixin from "../../../mixins/tableMixin.js";
    import axios from "axios";

    export default {
        name: "Tags",
        mixins: [tableMixin, tagsMixin], // shared table methods first, then Tags' own
        components: {
            vSelect,
            PageStatus,
            TableToolbar,
            Pagination,
            TableEmptyState,
            SortableTh,
            DeleteModal,
            RowActions,
            SearchBox,
            RecordFormModal,
            FormField,
            SelectField,
            HiddenField,
            FilterModal,
            ActiveFilters,
        },
        data() {
            return {
                // Standard data (isSaving, tableData, defaultAlert ...) comes from tableMixin
                standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
                apiUrl: "/tags/api_tags.php",     // used by tableMixin's fetchRecords, delete
                columnsKey: "tags",               // saves this page's hidden columns
                extraFilterKeys: ["modules"],     // extra filters in the Filter window
                formInputs: this.getDefaultFormInputs(),
                // Filled from the server (tagModules() in global_functions.php)
                moduleOptions: [{ label: "Crud Test", value: "crud" }],
                viewOptions: [
                    { label: "All Tags", value: "all" },
                    { label: "Tags By Me", value: "byme" },
                    { label: "Deleted Tags", value: "deletedone" },
                ],
                searchByOptions: [
                    { label: "Name", value: "name" },
                    { label: "Tag Number", value: "tag_number" },
                ],
            };
        },
        async created() {
            // Opened from a page's "Manage tags" link, e.g. /settings/tags/?module=crud
            const module = this.$route.query.module;
            if (typeof module === "string" && module) {
                this.tableData.modules = [module];
            }

            await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault
            );
        },
        computed: {
            pageTitle() {
                const titles = { all: "Tags", byme: "Tags By Me", deletedone: "Deleted Tags" };
                return titles[this.tableData.recordFilter] || "Tags";
            },
            tableColumns() {
                return [
                    { key: "name", label: "Tag" },
                    { key: "module", label: "Module" },
                    { key: "used_count", label: "Used on" },
                    { key: "date_created", label: "Date" },
                ];
            },
            // This page's own filters for the Filter button count (tableMixin counts the rest)
            pageFilterCount() {
                return this.tableData.modules.length ? 1 : 0;
            },
            // Extra label in the "Filtered by:" row (tableMixin adds it)
            pageFilterChips() {
                if (!this.tableData.modules.length) return [];
                const names = this.tableData.modules.map((v) => this.moduleOptions.find((o) => o.value === v)?.label || v);
                return [{ key: "modules", icon: "fa fa-th-large", label: `Module: ${names.join(", ")}` }];
            },
        },
        methods: {
            getDefaultFormInputs() {
                return {
                    id: "",
                    name: "",
                    color: "#0d6efd",
                    module: "crud",
                };
            },
                        // Table defaults: the standard ones (tableMixin) + this page's own
            getDefaultTableData() {
                return {
                    ...this.baseTableData(),
                    searchBy: "name",
                    searchPlaceHolderText: "Search by name",
                    modules: [], // module filter (empty = all)
                };
            },
             // × on the Module label
            removePageFilterChip(key) {
                if (key === "modules") this.tableData.modules = [];
            },
            // Tag text: dark on light colours (yellow, light green...), white on dark ones
            tagTextColor(hex) {
                const value = String(hex || "").replace("#", "");
                if (!/^[0-9a-f]{6}$/i.test(value)) return "#fff";
                const r = parseInt(value.slice(0, 2), 16);
                const g = parseInt(value.slice(2, 4), 16);
                const b = parseInt(value.slice(4, 6), 16);
                return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#212529" : "#fff";
            },
            // Sample button: a random tag name with a colour that suits it (a different one each press)
            addSampleData() {
                const samples = [
                    { name: "VIP", color: "#d4a017" },
                    { name: "Urgent", color: "#dc3545" },
                    { name: "Follow up", color: "#fd7e14" },
                    { name: "New customer", color: "#198754" },
                    { name: "Wholesale", color: "#6f42c1" },
                    { name: "Paid", color: "#20c997" },
                    { name: "On hold", color: "#6c757d" },
                    { name: "Priority", color: "#0d6efd" },
                    { name: "Returning", color: "#0dcaf0" },
                    { name: "Discount", color: "#ffc107" },
                ];
                const others = samples.filter((s) => s.name !== this.formInputs.name);
                const pick = others[Math.floor(Math.random() * others.length)];

                this.formInputs = {
                    ...this.formInputs,
                    name: pick.name,
                    color: pick.color,
                    // Keep the chosen module (fixed when editing); otherwise the first one
                    module: this.formInputs.module || this.moduleOptions[0]?.value || "crud",
                };

                this.submitted = false;
                this.$clearValidationStates("#recordForm");
                this.$scrollToTopModal("recordFormModal");
            },
            editRecord(record) {
                this.isSaving = false;
                this.isEditing = true;
                this.submitted = false;
                this.formInputs = {
                    id: record.id ?? "",
                    name: record.name ?? "",
                    color: record.color || "#0d6efd",
                    module: record.module ?? "crud",
                };
                this.defaultAlert = this.getDefaultAlert();
                this.$clearValidationStates("#recordForm");
            },
            formData_fields() {
                const formData = new FormData();
                formData.append("id", this.formInputs.id);
                formData.append("name", this.formInputs.name);
                formData.append("color", this.formInputs.color);
                formData.append("module", this.formInputs.module);
                formData.append("action", "saveOrEditRecord");
                return formData;
            },
            saveOrEditRecord() {
                if (this.isSaving) return;
                this.isSaving = true;

                this.$scrollToTopModal("recordFormModal");
                this.submitted = true;
                if (!this.$validateForm("#recordForm")) {
                    this.$playErrorSound();
                    this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                    this.isSaving = false;
                    return;
                }

                this.handleAlert("starting", "Please wait submiting", "info", "modal");
                if (!this.formInputs.id) this.tableData.currentPage = 1;

                axios
                    .post(this.apiUrl, this.formData_fields(), this.$handleConfig())
                    .then((response) => {
                        if (response.data.status == "success") {
                            this.handleSaveOrEditSuccess(response);
                        } else {
                            this.handleError("starting", response.data.message[0], "danger", "modal");
                        }
                    })
                    .catch((error) => {
                        this.handleAlert(
                            "ending",
                            error.message === "Network Error"
                                ? "No internet connection. Please check your network."
                                : "Something went wrong. Please try again later.",
                            "danger",
                            "modal"
                        );
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            },
            // This page's own filter, sent with every request (tableMixin adds the dates etc.)
            pageFilterParams() {
                return this.tableData.modules.length ? { modules: this.tableData.modules.join(",") } : {};
            },
                        // Module list from the server (tableMixin calls this after the table loads)
            afterFetch(response) {
                if (response.data.results.modules) {
                    this.moduleOptions = response.data.results.modules;
                }
            },
        },
    };
</script>